import React, {lazy, Suspense, useContext, useRef, useState} from 'react';
import {Picture} from '../Picture.jsx';
import {TrinityEvents} from '../../modules/teaser/trinity.js';
import {TrackingContext} from '../TrackingContext.jsx';
import {XRay} from '@teaser/scripts/components/XRay.jsx';

const MuxPlayer = lazy(() => import('@mux/mux-player-react'));
// _MUST NOT BE CHANGED_ because otherwise the tracking is broken
const VIDEO_WATCHTIME_TRACKING_INTERVAL = 30 * 1000;

/**
 * @typedef {Object} LongVideoModel
 * @property {VideoModel} videoModel
 * @property {ImageModel} thumbnailImage
 * @property {string} description
 * @property {string} entryId
 * @property {string} contentType
 */

/**
 * @param {LongVideoModel } model
 * @param {Device} device
 * @returns {JSX.Element}
 */
export function LongVideo({model, device}) {
    if (!model.videoModel) return <Fallback image={model.thumbnailImage}/>;

    const videoEl = useRef(null);
    let watchTimeTrackingInterval = null;
    let watchTime = 0;

    const [error, setError] = useState(false);

    const usedPlaybackId =
        window.innerWidth >= 768 && model.videoModel.overridePlaybackId
            ? model.videoModel.overridePlaybackId
            : model.videoModel.playbackId;

    const trackingContext = useContext(TrackingContext);

    const trinityAttributes = {
        isApp: device.name === 'app',
        object: 'video',
        teaserId: model.entryId,
        page: trackingContext.page,
    };
    let alreadyPlayed = false;

    function handlePlay() {
        const playEvent = alreadyPlayed ? 'resume' : 'start';
        TrinityEvents.sendVideoActionClick({
            ...trinityAttributes,
            clickName: playEvent,
        });
        alreadyPlayed = true;
        if (watchTimeTrackingInterval) {
            return;
        }

        watchTimeTrackingInterval = setInterval(() => {
            if (!isElementInViewport(videoEl)) return;

            TrinityEvents.sendVideoWatchtime({watchTime: ++watchTime, teaserId: model.entryId, ...trinityAttributes});
        }, VIDEO_WATCHTIME_TRACKING_INTERVAL);
    }

    function handlePause() {
        TrinityEvents.sendVideoActionClick({
            ...trinityAttributes,
            clickName: 'pause',
        });
        clearInterval(watchTimeTrackingInterval);
        watchTimeTrackingInterval = null;
    }

    function handleEnd() {
        clearInterval(watchTimeTrackingInterval);
    }

    return error ? (
        <Fallback backupImage={model.thumbnailImage}/>
    ) : (
        <Suspense fallback={<div/>}>
            <MuxPlayer
                preload="none"
                className={'content-platform__long-video'}
                streamType="on-demand"
                defaultStreamType="on-demand"
                disableCookies
                disableTracking
                ref={videoEl}
                onError={() => setError(true)}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnd}
                metadataVideoTitle={model.description ? model.description : ''}
                playbackId={usedPlaybackId}
                poster={`${model.thumbnailImage?.defaultImageSrc}?w=${window.innerWidth ?? 1168}` ?? ''}
            >
                <XRay fieldId="internalName" contentType={model.contentType} entryId={model.entryId} />
            </MuxPlayer>
        </Suspense>
    );
}

function Fallback({image}) {
    return image ? <Picture model={image}/> : <div className="content-platform__teaser__spacer"/>;
}

function isElementInViewport(domElement) {
    if (!domElement || !domElement.getBoundingClientRect) {
        return false;
    }
    return (
        domElement.getBoundingClientRect().top < (window.innerHeight || document.documentElement.clientHeight) && domElement.getBoundingClientRect().bottom >= 0
    );
}
